import { useEffect, useMemo, useRef } from 'react'
import { Link as RRLink } from 'react-router-dom'

import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useLazyQuery } from '@apollo/client/react'
import debounce from 'lodash/debounce'

import { Link, toast } from '@buffer-mono/popcorn'

import { PostCard_Post } from '~publish/components/PostCard/fragments/PostCard_Post'
import { useChannels } from '~publish/components/PublishSidebar/useChannels'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import type { FilterablePostType } from '~publish/helpers/postType'
import { isPostSatisfyingFilters } from '~publish/helpers/posts/isPostSatisfyingFilters'
import { reconcilePostScheduledTimeEcho } from '~publish/helpers/posts/recentPostScheduledTimeChanges'
import { isHeldAgainstTagFilterOnly } from '~publish/helpers/posts/tagEditVisibility'
import { wait } from '~publish/helpers/wait'
import { client } from '~publish/legacy/apollo-client'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import type {
  DateTimePresence,
  PostStatus,
  SortPreset,
} from './usePaginatedPostList'
import { GetPostList } from './usePaginatedPostList'

export const RealTimeUpdates_Post = graphql(`
  fragment RealTimeUpdates_Post on Post {
    id
    status
    dueAt
    metadata {
      ... on CommonPostMetadata {
        type
      }
    }
    tags {
      id
    }
    channel {
      id
    }
  }
`)

const RealTimeUpdatesScheduledTime_Post = graphql(`
  fragment RealTimeUpdatesScheduledTime_Post on Post {
    id
    dueAt
  }
`)

export const GetPostForRealTimeUpdate = graphql(
  `
    query GetPostForRealTimeUpdate($postId: PostId!) {
      post(input: { id: $postId }) {
        id
        dueAt
        sentAt
        # Keep in sync with GetPostList's node selection: this post is inserted
        # into that list's cache entry, so a missing fragment breaks the read.
        ...PostCard_Post
        ...CalendarPostCard_Post
        ...RealTimeUpdates_Post
      }
    }
  `,
  [PostCard_Post, CalendarPostCard_Post, RealTimeUpdates_Post],
)

type GetPostForRealTimeUpdateData = ResultOf<typeof GetPostForRealTimeUpdate>
type GetPostListData = ResultOf<typeof GetPostList>
type GetPostListVariables = VariablesOf<typeof GetPostList>

export const GetPostListForRealTimeUpdate = graphql(`
  query GetPostListForRealTimeUpdate(
    $first: Int!
    $after: String
    $organizationId: OrganizationId!
    $channelIds: [ChannelId!]
    $tags: TagComparator
    $status: [PostStatus!]
    $sort: [PostSortInput!]
    $dueAtPresence: DateTimePresence
    $postTypes: [PostType!]
  ) {
    posts(
      input: {
        organizationId: $organizationId
        filter: {
          channelIds: $channelIds
          tags: $tags
          status: $status
          dueAtPresence: $dueAtPresence
          postTypes: $postTypes
        }
        sort: $sort
      }
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          dueAt
        }
      }
    }
  }
`)

type GetPostListForRealTimeUpdateData = ResultOf<
  typeof GetPostListForRealTimeUpdate
>
type GetPostListForRealTimeUpdateVariables = VariablesOf<
  typeof GetPostListForRealTimeUpdate
>

/**
 * Pusher returns RpcUpdate data, but we only need the id
 * so we can use this id to refetch the post in the cache
 */
type PusherEventData = {
  update_id: string
  draft_id: string
  profile_id: string
}

// POST_CREATED is handled separately (see the dedicated usePusherEvent binding
// below): a bulk save fires one per created post, and coalescing that burst
// needs different handling than every other event here gets.
const postEvents = [
  PusherEvent.POST_UPDATED,
  PusherEvent.POST_TAG_ADDED,
  PusherEvent.POST_TAG_REMOVED,
  PusherEvent.POST_NOTE_ADDED,
  PusherEvent.POST_NOTE_DELETED,
  PusherEvent.POST_NOTE_UPDATED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.POST_SENT,
  PusherEvent.POST_FAILED,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_APPROVED,
]

/**
 * Evicts post from cache if it exists
 * @param id - The ID of the post to evict
 */
const evictPost = (id: string): void => {
  const normalizedId = client.cache.identify({
    id,
    __typename: 'Post',
  })

  // it is important to check that post exists in cache
  // if we try to evict non-existing post, queries that depend on it will reload
  const existingPost = client.cache.readFragment({
    id: normalizedId,
    fragment: graphql(`
      fragment Post_evicting on Post {
        id
      }
    `),
  })

  if (!existingPost) {
    return
  }

  client.cache.evict({ id: normalizedId })
}

/**
 * Highlights post as being removed
 * @param id - The ID of the post to highlight
 */
const highlightPostAsBeingRemoved = async (id: string): Promise<void> => {
  const normalizedId = client.cache.identify({
    id,
    __typename: 'Post',
  })

  if (!normalizedId) {
    return
  }

  client.cache.writeFragment({
    id: normalizedId,
    fragment: graphql(`
      fragment Post_isProcessing on Post {
        isProcessing @client
      }
    `),
    data: {
      isProcessing: true,
    },
  })

  // next tick, to make sure apollo cache is updated and there is time
  // to trigger react update before refetching
  await wait(0)
}

/**
 * Removes highlight from post
 * @param id - The ID of the post to remove highlight from
 */
const removePostHighlight = (id: string): void => {
  const normalizedId = client.cache.identify({
    id,
    __typename: 'Post',
  })

  if (!normalizedId) {
    return
  }

  // it is important to check that post exists in cache (same as evictPost):
  // writing the highlight flag to an evicted post would resurrect it as a
  // partial entity holding only `isProcessing`, leaving every list query
  // that still references it permanently incomplete (missing `id`), which
  // silently stops cache updates from reaching the rendered list
  const existingPost = client.cache.readFragment({
    id: normalizedId,
    fragment: graphql(`
      fragment Post_removingHighlight on Post {
        id
      }
    `),
  })

  if (!existingPost) {
    return
  }

  client.cache.writeFragment({
    id: normalizedId,
    fragment: graphql(`
      fragment Post_isProcessing on Post {
        isProcessing @client
      }
    `),
    data: {
      isProcessing: false,
    },
  })
}

function queryPost(
  postId: string,
): Promise<{ data: GetPostForRealTimeUpdateData | undefined }> {
  return client.query({
    query: GetPostForRealTimeUpdate,
    variables: { postId },
    fetchPolicy: 'no-cache',
  })
}

// Dedupes concurrent GetPostForRealTimeUpdate fetches for the same post id.
// Board mounts one useRealTimePusherUpdates per column, and a single Pusher
// event fans out to every mounted instance — without this, an org-wide post
// event on a 30-column Board would fire 30 identical uncached fetches for
// the same post. Scoped to in-flight requests only: once a fetch settles,
// its entry is cleared, so a later event still gets a fresh read.
const inFlightPostFetches = new Map<string, ReturnType<typeof queryPost>>()

function fetchPostDeduped(postId: string): ReturnType<typeof queryPost> {
  const existing = inFlightPostFetches.get(postId)
  if (existing) {
    return existing
  }

  const request = queryPost(postId).finally(() => {
    inFlightPostFetches.delete(postId)
  })

  inFlightPostFetches.set(postId, request)
  return request
}

// GraphQL extension code the backend attaches to a NotFoundError (see
// graphql/src/_utils/errors.ts). Matching on the code rather than the message
// keeps this resilient to copy changes.
const NOT_FOUND_ERROR_CODE = 'NOT_FOUND'

/**
 * True when the refetch failed specifically because the post no longer exists.
 * Other errors (forbidden, transient server failures) must not be treated as a
 * deletion, so they leave the cached post untouched.
 */
export const isPostNotFoundError = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some(
    (graphQLError) => graphQLError.extensions?.code === NOT_FOUND_ERROR_CODE,
  )

// How long a deleted post id stays in the guard. Long enough to cover Pusher
// delivery jitter between a post's delete event and any racing update event,
// short enough that the guard never grows unbounded on the org-wide stream.
export const DELETED_POST_GUARD_TTL_MS = 60_000

/**
 * Records a post id as recently deleted (with an expiry), pruning entries
 * whose window has passed. This keeps the guard bounded by the delete rate
 * within the TTL rather than by total session volume.
 */
export const rememberDeletedPost = (
  deletedPosts: Map<string, number>,
  id: string,
): void => {
  const now = Date.now()
  for (const [postId, expiresAt] of deletedPosts) {
    if (expiresAt <= now) {
      deletedPosts.delete(postId)
    }
  }
  deletedPosts.set(id, now + DELETED_POST_GUARD_TTL_MS)
}

/**
 * Returns true if the post was deleted within the guard window. Expired
 * entries are dropped on access so the map self-heals without timers.
 */
export const wasRecentlyDeleted = (
  deletedPosts: Map<string, number>,
  id: string,
): boolean => {
  const expiresAt = deletedPosts.get(id)
  if (expiresAt === undefined) {
    return false
  }
  if (expiresAt <= Date.now()) {
    deletedPosts.delete(id)
    return false
  }
  return true
}

// The client-side subset of `PostsFiltersInput` these updates match against —
// the same shape `isPostSatisfyingFilters` consumes (`tagIds`, not the query's
// `tags`/TagComparator).
type PostListFilters = {
  channelIds?: string[] | null
  status?: PostStatus[] | null
  tagIds?: string[] | null
  dueAtPresence?: DateTimePresence | null
  postTypes?: readonly FilterablePostType[] | null
}

type UseRealTimePusherUpdatesOptions = {
  filters: PostListFilters
  refetch: (options?: {
    mode?: 'remove' | 'insert'
    // Number of posts inserted at once — a coalesced POST_CREATED burst asks
    // the list to fetch that many extra, instead of the default one.
    insertCount?: number
  }) => Promise<void>
  isPostVisible: (id: string) => boolean
  variables?: GetPostListVariables
  sort: SortPreset
}

// Debounce window for coalescing a burst of POST_CREATED events (e.g. a post
// saved across many channels at once, CORE-7054): short enough to still feel
// immediate for a single post, long enough to catch the fan-out from a large
// bulk save.
const CREATED_EVENTS_DEBOUNCE_MS = 300
// Upper bound on how long a steady trickle of events can keep deferring the
// flush, so a very spread-out burst still resolves promptly.
const CREATED_EVENTS_MAX_WAIT_MS = 2_000

/**
 * Hook that handles real-time updates to posts via Pusher events
 * Manages post visibility, ordering, and cache updates based on various events
 */
export const useRealTimePusherUpdates = ({
  filters,
  isPostVisible,
  refetch,
  variables,
  sort,
}: UseRealTimePusherUpdatesOptions): void => {
  const organizationId = useOrganizationId()
  const { channels } = useChannels()
  const isPostVisibleRef = useRef(isPostVisible)
  isPostVisibleRef.current = isPostVisible
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  // Posts this client has observed being deleted (id -> expiry), so realtime
  // events that race with the deletion skip a doomed read-after-delete refetch.
  const deletedPosts = useRef<Map<string, number>>(new Map())

  const [getPostList] = useLazyQuery(GetPostListForRealTimeUpdate, {
    fetchPolicy: 'network-only',
  })

  // Ids from POST_CREATED events accumulated since the last flush. A ref, not
  // state: accumulating and flushing must not trigger renders of their own.
  const pendingCreatedIds = useRef<Set<string>>(new Set())
  // Assigned after `updatePost` is defined below; declared here so
  // `flushCreatedPosts` can close over a stable ref instead of the function.
  const updatePostRef = useRef<(id: string) => Promise<void>>(() =>
    Promise.resolve(),
  )

  // Coalesces a POST_CREATED burst (e.g. a post saved across many channels at
  // once) into a single list refetch instead of one `updatePost` round trip
  // per post. `leading: true` means a lone event is handled by the exact same
  // precise-insert path as today, with no added latency — the trailing edge
  // only fires when a *second* event arrives inside the debounce window, so a
  // single post creation never waits on it.
  const flushCreatedPosts = useMemo(
    () =>
      debounce(
        () => {
          const ids = Array.from(pendingCreatedIds.current)
          pendingCreatedIds.current.clear()
          if (ids.length === 0) return
          if (ids.length === 1) {
            void updatePostRef.current(ids[0])
            return
          }
          void refetchRef.current({ mode: 'insert', insertCount: ids.length })
        },
        CREATED_EVENTS_DEBOUNCE_MS,
        { leading: true, trailing: true, maxWait: CREATED_EVENTS_MAX_WAIT_MS },
      ),
    [],
  )
  useEffect(() => (): void => flushCreatedPosts.cancel(), [flushCreatedPosts])

  /**
   * Updates a post's data and manages its visibility in the list
   * @param id - The ID of the post to update
   */
  const updatePost = async (id: string): Promise<void> => {
    // Skip posts already known to be deleted: the refetch would only
    // read-after-delete and return "not found".
    if (wasRecentlyDeleted(deletedPosts.current, id)) {
      return
    }

    // fetching one post to get its data after update. The execute promise
    // rejects on a GraphQL error, so the failure path is reached via catch
    // rather than a returned `error` field.
    let data: GetPostForRealTimeUpdateData | undefined
    let error: unknown
    try {
      ;({ data } = await fetchPostDeduped(id))
    } catch (caught) {
      error = caught
    }

    const updatedPost = data?.post
    if (!updatedPost) {
      // A NOT_FOUND means the post was deleted server-side while we were
      // watching it (a realtime event raced ahead of its POST_DELETED). Treat
      // it as a deletion so a missed or late delete event can't leave a ghost
      // row, and remember it so further events for this id skip the refetch.
      if (isPostNotFoundError(error)) {
        rememberDeletedPost(deletedPosts.current, id)
        evictPost(id)
        return
      }
      // Surface every other error (forbidden, transient server failures)
      // instead of swallowing it, so only the deleted-post case is special.
      if (error) {
        throw error
      }
      return
    }

    // Read cache state after the network request. A local drag can update the
    // post and its connection membership while this request is in flight.
    const postCacheId = client.cache.identify({
      __typename: 'Post',
      id,
    })
    const persistedPost = client.cache.readFragment({
      id: postCacheId,
      fragment: RealTimeUpdatesScheduledTime_Post,
    })
    const visiblePost = client.cache.readFragment({
      optimistic: true,
      id: postCacheId,
      fragment: RealTimeUpdatesScheduledTime_Post,
    })
    const wasPostVisibleBeforeUpdate = isPostVisibleRef.current(id)

    const {
      dueAt,
      dueAtForCache,
      isStale: isStaleScheduledTimeEcho,
    } = reconcilePostScheduledTimeEcho(
      id,
      updatedPost.dueAt,
      persistedPost,
      visiblePost,
    )
    const postForUpdate = isStaleScheduledTimeEcho
      ? { ...updatedPost, dueAt }
      : updatedPost
    const postForCache =
      dueAtForCache !== updatedPost.dueAt
        ? { ...updatedPost, dueAt: dueAtForCache }
        : updatedPost
    const dataForCache =
      postForCache !== updatedPost ? { ...data, post: postForCache } : data

    const { satisfies, filterViolations } = isPostSatisfyingFilters(
      readFragment(RealTimeUpdates_Post, postForUpdate),
      filters,
    )

    // While a post's tag menu is open we pin it visible — but only against a
    // *tag-filter* mismatch, so several tags can be added in a row without the
    // row vanishing. Every other change (sent, rescheduled, moved to another
    // channel) still flows through the normal handling below, so an edited row
    // can't get stuck out of sync. The deferred tag removal runs on menu close
    // (see PostCardTags / removePostFromMismatchedTagLists).
    const shouldUpdatedPostBeVisible =
      satisfies || isHeldAgainstTagFilterOnly(id, filterViolations)

    // case 1: post was visible but should not be visible anymore after update (due to filters)
    // -> highlight post as being removed, refetch the list and remove highlight
    if (wasPostVisibleBeforeUpdate && !shouldUpdatedPostBeVisible) {
      await highlightPostAsBeingRemoved(id)
      await removePostFromCache(id)
      removePostHighlight(id)

      return
    }

    // case 2: post was not visible before, but should be visible now
    // -> refetch the list, because order is not clear
    if (!wasPostVisibleBeforeUpdate && shouldUpdatedPostBeVisible) {
      insertNewPostInCache(postForUpdate, variables, sort)

      return
    }

    // case 3: post was visble but its due date was changed which impacts its order in the list
    // -> refetch the list, because order is likely to change
    if (
      wasPostVisibleBeforeUpdate &&
      updatedPost &&
      visiblePost &&
      visiblePost.dueAt !== dueAt
    ) {
      updateCachedPost(id, dataForCache)
      updatePostListOrder(variables, sort)

      return
    }

    // case 4: all other cases, likely just post content has changed
    // -> update post data in apollo cache
    updateCachedPost(id, dataForCache)
  }
  updatePostRef.current = updatePost

  /**
   * Checks if a channel exists on the fetched channels list
   * @param profileId - The ID of the profile
   * @returns True if the channel exists, false otherwise
   */
  // TODO: this is a temporary solution, because pusher events are sent to all
  // users of the organization regardless of which channels they have access to
  // we should update pusher events to be more specific to the user
  const isChannelAllowed = (profileId?: string): boolean => {
    return (
      profileId !== undefined &&
      channels.find((c) => c.id === profileId) !== undefined
    )
  }

  usePusherEvent(
    PusherEvent.POST_DELETED,
    async (data: PusherEventData): Promise<void> => {
      debugLog('💽 POST_DELETED', data)
      const id = data.update_id

      // Remember the deletion so any racing realtime event for this post
      // skips its refetch (see updatePost).
      rememberDeletedPost(deletedPosts.current, id)

      // highlight post as being removed, wait a bit and then remove it from cache
      await removePostFromCache(id)
    },
  )

  usePusherEvent(
    PusherEvent.POSTS_REORDERED,
    async (data?: {
      profile_id?: string
      update_ids?: Array<string>
    }): Promise<void> => {
      debugLog('💽 POSTS_REORDERED', data)
      const hasChanges = Number(data?.update_ids?.length) > 0
      const isProfileIncluded =
        // no filters means all channles are included
        filters?.channelIds === undefined ||
        Boolean(filters?.channelIds?.length) ||
        filters?.channelIds?.includes(data?.profile_id ?? '')
      if (
        !hasChanges ||
        !isProfileIncluded ||
        !isChannelAllowed(data?.profile_id)
      ) {
        return
      }

      if (data?.update_ids?.length === 1) {
        updatePost(data.update_ids[0])
      } else {
        if (!organizationId) return
        fetchPostListOrder(getPostList, organizationId, variables)
      }
    },
  )

  usePusherEvent(PusherEvent.POST_CREATED, (data: PusherEventData): void => {
    debugLog('💽 POST_CREATED', data)

    if (!isChannelAllowed(data?.profile_id)) {
      return
    }

    pendingCreatedIds.current.add(data.update_id ?? data.draft_id)
    flushCreatedPosts()
  })

  usePusherEvent(
    PusherEvent.QUEUE_CHANGED,
    async (data?: { profile_id?: string }): Promise<void> => {
      debugLog('💽 QUEUE_CHANGED', data)

      const isProfileIncluded =
        // no filters means all channles are included
        filters?.channelIds?.length === 0 ||
        filters?.channelIds?.includes(data?.profile_id ?? '')
      if (!isProfileIncluded) {
        return
      }

      refetch()
    },
  )

  usePusherEvent(postEvents, (data: PusherEventData): void => {
    debugLog('💽 POST_EVENTS', data)

    if (!isChannelAllowed(data?.profile_id)) {
      return
    }
    updatePost(data.update_id ?? data.draft_id)
  })

  usePusherEvent(PusherEvent.POST_FAILED, (data: PusherEventData): void => {
    debugLog('💽 POST_FAILED_EVENT', data)

    if (!isChannelAllowed(data?.profile_id)) {
      return
    }

    const channel: { id: string; name: string } | undefined = channels.find(
      (c) => c.id === data.profile_id,
    )

    if (channel) {
      const message = `Your post for ${channel.name} has failed to publish.`
      const deepLink = `/channels/${channel.id}`
      toast.error(
        <div>
          {message}{' '}
          <Link as={RRLink} to={deepLink}>
            {' '}
            Learn more{' '}
          </Link>
        </div>,
      )
    }
  })
}

interface SortablePost {
  node: {
    dueAt?: string | null
    createdAt?: string | null
  }
}

// Create a sorting function for posts based on the sort parameter
function getSortFunction(
  sort: 'upcomingFirst' | 'mostRecentlyPostedFirst',
): (a: SortablePost, b: SortablePost) => number {
  return sort === 'upcomingFirst'
    ? (a: SortablePost, b: SortablePost): number =>
        (a.node.dueAt ?? '').localeCompare(b.node.dueAt ?? '') ||
        (b.node.createdAt ?? '').localeCompare(a.node.createdAt ?? '')
    : (a: SortablePost, b: SortablePost): number =>
        (b.node.dueAt ?? '').localeCompare(a.node.dueAt ?? '') ||
        (b.node.createdAt ?? '').localeCompare(a.node.createdAt ?? '')
}

/**
 * Removes a post from the Apollo cache with visual feedback
 * @param id - The ID of the post to remove
 */
async function removePostFromCache(id: string): Promise<void> {
  await highlightPostAsBeingRemoved(id)
  await wait(500)
  evictPost(id)
}

/**
 * Inserts a new post into the Apollo cache while maintaining sort order
 * @param updatedPost - The post to insert
 * @param variables - Variables for the post list query
 * @param sort - Sorting function for the post list
 */
function insertNewPostInCache(
  updatedPost: GetPostForRealTimeUpdateData['post'],
  variables?: GetPostListVariables,
  sort: SortPreset = 'upcomingFirst',
): void {
  const existingData = client.cache.readQuery<GetPostListData>({
    query: GetPostList,
    variables,
  })

  if (!existingData?.posts.edges) {
    // If there are no edges, the query was never made, so we don't need to update the cache
    return
  }

  const newPost = {
    __typename: 'PostsEdge' as const,
    node: {
      __typename: 'Post' as const,
      ...updatedPost,
    },
  }

  // Check if post belongs beyond the current pagination boundary.
  // When hasNextPage is true, inserting posts beyond the boundary creates gaps
  // in the list where unloaded posts should appear, causing confusing empty slots.
  // Instead, skip insertion - the post will appear when the user scrolls to that page.
  // The comparator covers the full sort tuple (dueAt, then createdAt DESC), so
  // lists where every dueAt ties — like the all-undated Calendar drafts
  // sidebar — still honour the boundary via createdAt. A strict > keeps exact
  // ties in the current page.
  const hasNextPage = existingData.posts.pageInfo.hasNextPage
  const lastEdge = existingData.posts.edges.at(-1)
  const compareFn = getSortFunction(sort)

  if (hasNextPage && lastEdge && compareFn(newPost, lastEdge) > 0) {
    return
  }

  // Remove any existing post with same ID (deduplication)
  const filteredEdges = existingData.posts.edges.filter(
    (edge) => edge.node.id !== updatedPost.id,
  )

  // Binary search to find insertion position - O(log n)
  let left = 0
  let right = filteredEdges.length

  while (left < right) {
    const mid = Math.floor((left + right) / 2)
    if (compareFn(newPost, filteredEdges[mid]) < 0) {
      right = mid
    } else {
      left = mid + 1
    }
  }

  // Create new edges array with post inserted at correct position
  const updatedEdges = [
    ...filteredEdges.slice(0, left),
    newPost,
    ...filteredEdges.slice(left),
  ]

  client.cache.writeQuery({
    query: GetPostList,
    variables,
    data: {
      ...existingData,
      posts: {
        ...existingData.posts,
        edges: updatedEdges,
      },
    },
  })
}

/**
 * Updates a post's data in the Apollo cache
 * @param id - The ID of the post to update
 * @param data - The new post data
 */
function updateCachedPost(
  id: string,
  data: GetPostForRealTimeUpdateData | undefined,
): void {
  client.cache.writeQuery({
    query: GetPostForRealTimeUpdate,
    variables: {
      postId: id,
    },
    data,
  })
}

/**
 * Updates the order of posts in the list based on their due dates
 * @param variables - Variables for the post list query
 */
function updatePostListOrder(
  variables?: GetPostListVariables,
  sort: SortPreset = 'upcomingFirst',
): void {
  const existingData = client.cache.readQuery<GetPostListData>({
    query: GetPostList,
    variables,
  })

  if (existingData?.posts.edges) {
    client.cache.writeQuery({
      query: GetPostList,
      variables,
      data: {
        ...existingData,
        posts: {
          ...existingData.posts,
          edges: existingData.posts.edges.toSorted(getSortFunction(sort)),
        },
      },
    })
  }
}

/**
 * Fetches updated post list order from the server with only the id and dueAt
 * for each post and updates the cache
 * @param getPostList - Function to fetch the post list
 * @param organizationId - The ID of the organization
 * @param variables - Variables for the post list query
 */
async function fetchPostListOrder(
  getPostList: useLazyQuery.ExecFunction<
    GetPostListForRealTimeUpdateData,
    GetPostListForRealTimeUpdateVariables
  >,
  organizationId: string,
  variables?: GetPostListVariables,
  sort: SortPreset = 'upcomingFirst',
): Promise<void> {
  const existingData = client.cache.readQuery<GetPostListData>({
    query: GetPostList,
    variables,
  })

  // Refetch dueAt for all posts in the cache
  const { data: updatedData } = await getPostList({
    variables: {
      ...variables,
      first: (existingData?.posts.edges?.length ?? 0) + 1,
      organizationId,
    },
  })

  if (!existingData?.posts.edges) {
    // If there are no edges, the query was never made, so we don't need to update the cache
    return
  }

  const updatedDueAtMap =
    updatedData?.posts.edges?.reduce<Record<string, string | null | undefined>>(
      (acc, edge) => {
        acc[edge.node.id] = edge.node.dueAt
        return acc
      },
      {},
    ) ?? {}

  const newEdges = existingData.posts.edges
    .map((edge) => {
      if (updatedDueAtMap[edge.node.id]) {
        return {
          ...edge,
          node: {
            ...edge.node,
            dueAt: updatedDueAtMap[edge.node.id],
          },
        }
      }
      return edge
    })
    .toSorted(getSortFunction(sort))

  client.cache.modify({
    id: client.cache.identify({
      id: existingData.posts.edges[0].node.id,
      __typename: 'Post',
    }),
    fields: {
      edges: () => newEdges,
    },
  })
}
